import React, { useState, useEffect, useMemo } from 'react';
import {
    Container,
    Paper,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    Box,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    CircularProgress,
    Snackbar,
    Tooltip,
    Grid,
    FormControlLabel,
    Checkbox,
    Autocomplete
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    FilterList as FilterIcon,
    CloudUpload as UploadIcon,
    AttachFile as AttachIcon,
    Close as CloseIcon,
    GetApp as DownloadIcon
} from '@mui/icons-material';
import { useOrganization } from '../../context/OrganizationContext';
import { useProfile } from '../../context/userContext';
import { useAuth0 } from '@auth0/auth0-react';
import {
    getIncidents,
    reportIncident,
    updateIncident,
    updateIncidentStatus,
    deleteIncident
} from '../../network/apis/compliance.api';
import { useToast } from '../../hooks/useToast';
import { DateTime } from 'luxon';

// FAA Part 107.9 and NTSB 49 CFR 830.5 compliant severity levels
const SEVERITY_LEVELS = [
    { value: 'Not Reportable', label: 'Not Reportable', description: 'Minor incidents below FAA thresholds' },
    { value: 'Part 107 Reportable', label: 'Part 107 Reportable', description: 'Serious injury, loss of consciousness, or property damage >$500' },
    { value: 'NTSB Reportable', label: 'NTSB Reportable', description: 'Meets 49 CFR 830.5 criteria for substantial damage' },
    { value: 'Critical', label: 'Critical', description: 'Public safety threat or emergency responder interference' }
];

// FAA AIP Part 2 ENR 8.8 compliant incident types
const INCIDENT_TYPES = [
    { value: 'Crash/Loss of Control', label: 'Crash/Loss of Control', description: 'UAS crash or uncontrolled descent' },
    { value: 'Midair Collision/Near Miss', label: 'Midair Collision/Near Miss', description: 'Encounter with aircraft or obstacles' },
    { value: 'Equipment Malfunction', label: 'Equipment Malfunction', description: 'System or component failure during flight' },
    { value: 'Serious Injury/Loss of Consciousness', label: 'Serious Injury/Loss of Consciousness', description: 'Physical harm or medical emergency (FAA reportable)' },
    { value: 'Property Damage', label: 'Property Damage', description: 'Damage to non-UAS property (>$500 is FAA reportable)' },
    { value: 'Flyaway/Lost Link', label: 'Flyaway/Lost Link', description: 'Loss of control link or GPS signal' },
    { value: 'Airspace Violation', label: 'Airspace Violation', description: 'Unauthorized airspace entry' },
    { value: 'Interference with Manned Aircraft', label: 'Interference with Manned Aircraft', description: 'Proximity to emergency or other aircraft' },
    { value: 'Other', label: 'Other', description: 'Other incidents not categorized above' }
];

const INCIDENT_STATUSES = ['Open', 'Under Investigation', 'Resolved', 'Closed'];

const Incidents = () => {
    const { currentOrganization, userRole } = useOrganization();
    const { profile } = useProfile();
    const { getAccessTokenSilently } = useAuth0();
    const { toast, showSuccess, showError, hideToast } = useToast();

    // Get all available IANA timezones from browser
    const timezoneOptions = useMemo(() => {
        try {
            // Get all IANA timezones supported by the browser
            const timezones = Intl.supportedValuesOf('timeZone');

            // Format for display with offset
            return timezones.map(tz => {
                try {
                    const now = new Date();
                    const offset = new Intl.DateTimeFormat('en-US', {
                        timeZone: tz,
                        timeZoneName: 'shortOffset'
                    }).formatToParts(now).find(part => part.type === 'timeZoneName')?.value || '';

                    return {
                        value: tz,
                        label: `${tz.replace(/_/g, ' ')} (${offset})`
                    };
                } catch (e) {
                    return { value: tz, label: tz.replace(/_/g, ' ') };
                }
            }).sort((a, b) => a.label.localeCompare(b.label));
        } catch (e) {
            // Fallback to browser's default timezone
            const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            return [{ value: userTz, label: userTz }];
        }
    }, []);

    const [incidents, setIncidents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reportDialogOpen, setReportDialogOpen] = useState(false);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedIncident, setSelectedIncident] = useState(null);
    const [formData, setFormData] = useState({
        incidentDate: '',
        incidentTimezone: '',
        severity: 'Not Reportable',
        incidentType: 'Other',
        location: '',
        description: '',
        damages: '',
        injuries: '',
        faaNotified: false
    });
    const [attachments, setAttachments] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
    const [viewingDocumentUrl, setViewingDocumentUrl] = useState(null);

    // Permissions
    const canReport = ['pilot', 'manager', 'admin', 'owner'].includes(userRole?.toLowerCase());
    const canDelete = ['admin', 'owner'].includes(userRole?.toLowerCase());

    // Function to check if user can update a specific incident
    const canUpdateIncident = (incident) => {
        const role = userRole?.toLowerCase();
        // Managers+ can update any incident
        if (['manager', 'admin', 'owner'].includes(role)) {
            return true;
        }
        // Pilots can only update their own incidents
        if (role === 'pilot' && incident.reporter?.id === profile?.userId) {
            return true;
        }
        return false;
    };

    useEffect(() => {
        if (currentOrganization?.organizationId) {
            fetchIncidents();
        }
    }, [currentOrganization, userRole, profile]);

    const fetchIncidents = async () => {
        try {
            setLoading(true);
            const token = await getAccessTokenSilently();
            const data = await getIncidents(token);

            // Filter incidents based on role
            let filteredIncidents = data.incidents || [];

            // Pilots can only view their own incidents
            if (userRole?.toLowerCase() === 'pilot' && profile?.userId) {
                filteredIncidents = filteredIncidents.filter(incident =>
                    incident.reportedBy?.id === profile.userId
                );
            }

            setIncidents(filteredIncidents);
        } catch (error) {
            showError('Failed to load incidents');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenReportDialog = () => {
        // Get current datetime in local timezone formatted for datetime-local input
        const now = new Date();
        const localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
            .toISOString()
            .slice(0, 16); // Format: YYYY-MM-DDTHH:MM

        // Get user's current timezone
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        setFormData({
            incidentDate: localDateTime,
            incidentTimezone: userTimezone,
            severity: 'Not Reportable',
            incidentType: 'Other',
            location: '',
            description: '',
            damages: '',
            injuries: '',
            faaNotified: false
        });
        setAttachments([]);
        setUploadError(null);
        setReportDialogOpen(true);
    };

    const handleCloseReportDialog = () => {
        setReportDialogOpen(false);
        setAttachments([]);
        setUploadError(null);
    };

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handleFileSelect = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;

        // Validate file types and sizes
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'video/mp4', 'video/quicktime'];
        const maxSize = 20 * 1024 * 1024; // 20MB per file

        for (const file of files) {
            if (!allowedTypes.includes(file.type)) {
                setUploadError(`File "${file.name}" is not a supported type. Please upload PDF, images, or videos.`);
                return;
            }
            if (file.size > maxSize) {
                setUploadError(`File "${file.name}" exceeds 20MB limit.`);
                return;
            }
        }

        setUploadError(null);
        setUploading(true);

        try {
            const token = await getAccessTokenSilently();
            const uploadedFiles = [];

            for (const file of files) {
                // Step 1: Request presigned URL from backend
                const urlResponse = await fetch(
                    `${process.env.REACT_APP_API_URL}/api/organization/${currentOrganization.organizationUuid}/files/upload-url`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            fileName: file.name,
                            fileType: file.type,
                            fileSize: file.size,
                            category: 'documents'
                        })
                    }
                );

                if (!urlResponse.ok) {
                    const errorData = await urlResponse.json();
                    throw new Error(errorData.error || 'Failed to get upload URL');
                }

                const { presignedUrl, fileUrl } = await urlResponse.json();

                // Step 2: Upload file directly to S3
                const uploadResponse = await fetch(presignedUrl, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': file.type
                    },
                    body: file
                });

                if (!uploadResponse.ok) {
                    throw new Error(`Failed to upload ${file.name}`);
                }

                // Step 3: Add to attachments list
                uploadedFiles.push({
                    fileName: file.name,
                    fileUrl: fileUrl,
                    uploadedAt: new Date().toISOString()
                });
            }

            setAttachments([...attachments, ...uploadedFiles]);
            showSuccess(`${uploadedFiles.length} file(s) uploaded successfully`);
        } catch (err) {
            setUploadError(err.message);
        } finally {
            setUploading(false);
            // Reset file input
            event.target.value = '';
        }
    };

    const handleRemoveAttachment = (index) => {
        setAttachments(attachments.filter((_, i) => i !== index));
    };

    const handleViewAttachment = async (fileUrl) => {
        try {
            // Extract fileKey from the S3 URL
            const url = new URL(fileUrl);
            const fileKey = url.pathname.substring(1); // Remove leading '/'

            // Get presigned URL for viewing
            const token = await getAccessTokenSilently();
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/organization/${currentOrganization.organizationUuid}/files/view-url?fileKey=${encodeURIComponent(fileKey)}`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (!response.ok) {
                throw new Error('Failed to get view URL');
            }

            const data = await response.json();
            const viewUrl = data.url || data.presignedUrl;

            // Open document in viewer modal
            setViewingDocumentUrl(viewUrl);
            setDocumentViewerOpen(true);
        } catch (error) {
            showError('Failed to load attachment: ' + error.message);
        }
    };

    // Helper function to check if file is an image
    const isImageFile = (url) => {
        if (!url) return false;
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
        const urlLower = url.toLowerCase();
        return imageExtensions.some(ext => urlLower.includes(ext));
    };

    const handleReportIncident = async () => {
        try {
            const token = await getAccessTokenSilently();

            // Convert the datetime-local value to UTC using the selected timezone
            // The datetime-local input gives us YYYY-MM-DDTHH:MM format
            // We need to interpret it AS IF it's in the selected timezone, then convert to UTC
            let incidentDateUTC = formData.incidentDate;

            if (formData.incidentTimezone) {
                // Parse the datetime AS IF it's in the selected timezone
                const dt = DateTime.fromISO(formData.incidentDate, { zone: formData.incidentTimezone });
                // Convert to UTC ISO string for backend
                incidentDateUTC = dt.toUTC().toISO();
            }

            // Include attachments in the incident report
            const reportData = {
                ...formData,
                incidentDate: incidentDateUTC,
                attachments: attachments.length > 0 ? attachments : null
            };

            await reportIncident(token, reportData);
            showSuccess('Incident reported successfully');
            handleCloseReportDialog();
            fetchIncidents();
        } catch (error) {
            showError('Failed to report incident');
        }
    };

    const handleViewIncident = (incident) => {
        setSelectedIncident(incident);
        setViewDialogOpen(true);
    };

    const handleStatusChange = async (incidentId, newStatus) => {
        try {
            const token = await getAccessTokenSilently();
            await updateIncidentStatus(token, incidentId, { status: newStatus });
            showSuccess('Incident status updated');
            fetchIncidents();
        } catch (error) {
            showError('Failed to update status');
        }
    };

    const handleDeleteIncident = async () => {
        try {
            const token = await getAccessTokenSilently();
            await deleteIncident(token, selectedIncident.id);
            showSuccess('Incident deleted successfully');
            setDeleteDialogOpen(false);
            setSelectedIncident(null);
            fetchIncidents();
        } catch (error) {
            showError('Failed to delete incident');
        }
    };

    const getSeverityColor = (severity) => {
        switch (severity) {
            case 'Critical': return 'error';
            case 'Major': return 'warning';
            case 'Moderate': return 'info';
            case 'Minor': return 'success';
            default: return 'default';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Open': return 'error';
            case 'Under Investigation': return 'warning';
            case 'Resolved': return 'success';
            case 'Closed': return 'default';
            default: return 'default';
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString();
    };

    const formatDateTime = (dateString, timezone = null) => {
        const date = new Date(dateString);
        const options = {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZoneName: 'short',
            ...(timezone && { timeZone: timezone })
        };
        return date.toLocaleString(undefined, options);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                    <Typography variant="h4" gutterBottom>
                        Incident Reports
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Track and manage safety incidents and near misses
                    </Typography>
                </Box>
                {canReport && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenReportDialog}
                    >
                        Report Incident
                    </Button>
                )}
            </Box>

            {/* Pilot viewing restriction notice */}
            {userRole?.toLowerCase() === 'pilot' && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                        <strong>Note:</strong> You can only view incidents that you have reported. To view all organization incidents, contact your Manager or Admin.
                    </Typography>
                </Alert>
            )}

            {/* FAA Reporting Requirements Info */}
            <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                    <strong>FAA Part 107 Reporting Requirements (14 CFR 107.9)</strong>
                </Typography>
                <Typography variant="body2">
                    You must report to the FAA within 10 days if your UAS operation results in:
                </Typography>
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
                    <li>
                        <Typography variant="body2">
                            <strong>Serious injury</strong> or <strong>loss of consciousness</strong> to any person
                        </Typography>
                    </li>
                    <li>
                        <Typography variant="body2">
                            <strong>Property damage</strong> exceeding <strong>$500</strong> (excluding the UAS itself)
                        </Typography>
                    </li>
                </Box>
                <Typography variant="caption" color="text.secondary">
                    For more information, visit{' '}
                    <a href="https://www.faa.gov/Air_traffic/publications/atpubs/aim_html/chap11_section_8.html" target="_blank" rel="noopener noreferrer">
                        FAA UAS Reporting
                    </a>
                </Typography>
            </Alert>

            {incidents.length === 0 ? (
                <Alert severity="info">No incidents reported.</Alert>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Date & Time</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Severity</TableCell>
                                <TableCell>Location</TableCell>
                                <TableCell>Reporter</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {incidents.map((incident) => (
                                <TableRow key={incident.id}>
                                    <TableCell>{formatDateTime(incident.incidentDate, incident.incidentTimezone)}</TableCell>
                                    <TableCell>{incident.incidentType}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={incident.severity}
                                            color={getSeverityColor(incident.severity)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>{incident.location || '-'}</TableCell>
                                    <TableCell>
                                        {incident.reporter?.firstName} {incident.reporter?.lastName}
                                    </TableCell>
                                    <TableCell>
                                        {canUpdateIncident(incident) ? (
                                            <TextField
                                                select
                                                value={incident.status}
                                                onChange={(e) => handleStatusChange(incident.id, e.target.value)}
                                                size="small"
                                                variant="outlined"
                                                sx={{ minWidth: 150 }}
                                            >
                                                {INCIDENT_STATUSES.map((status) => (
                                                    <MenuItem key={status} value={status}>
                                                        {status}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        ) : (
                                            <Chip
                                                label={incident.status}
                                                color={getStatusColor(incident.status)}
                                                size="small"
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="View Details">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleViewIncident(incident)}
                                            >
                                                <ViewIcon />
                                            </IconButton>
                                        </Tooltip>
                                        {canDelete && (
                                            <Tooltip title="Delete Incident">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => {
                                                        setSelectedIncident(incident);
                                                        setDeleteDialogOpen(true);
                                                    }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Report Incident Dialog */}
            <Dialog open={reportDialogOpen} onClose={() => setReportDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Report Incident</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Incident Date & Time"
                                    name="incidentDate"
                                    type="datetime-local"
                                    value={formData.incidentDate}
                                    onChange={handleChange}
                                    InputLabelProps={{ shrink: true }}
                                    helperText="Enter the local time when the incident occurred"
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    options={timezoneOptions}
                                    getOptionLabel={(option) => option.label}
                                    value={timezoneOptions.find(tz => tz.value === formData.incidentTimezone) || null}
                                    onChange={(event, newValue) => {
                                        setFormData({
                                            ...formData,
                                            incidentTimezone: newValue ? newValue.value : ''
                                        });
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Incident Timezone"
                                            required
                                            helperText="Timezone where the incident occurred"
                                        />
                                    )}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Severity (FAA Part 107 Classification)"
                                    name="severity"
                                    value={formData.severity}
                                    onChange={handleChange}
                                    required
                                    helperText="Select based on FAA Part 107.9 reporting requirements"
                                >
                                    {SEVERITY_LEVELS.map((level) => (
                                        <MenuItem key={level.value} value={level.value}>
                                            <Box>
                                                <Typography variant="body2">{level.label}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {level.description}
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Incident Type"
                                    name="incidentType"
                                    value={formData.incidentType}
                                    onChange={handleChange}
                                    required
                                    helperText="Select the primary incident category"
                                >
                                    {INCIDENT_TYPES.map((type) => (
                                        <MenuItem key={type.value} value={type.value}>
                                            <Box>
                                                <Typography variant="body2">{type.label}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {type.description}
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Location"
                                    name="location"
                                    value={formData.location}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Description"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    multiline
                                    rows={4}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Damages (if any)"
                                    name="damages"
                                    value={formData.damages}
                                    onChange={handleChange}
                                    multiline
                                    rows={2}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Injuries (if any)"
                                    name="injuries"
                                    value={formData.injuries}
                                    onChange={handleChange}
                                    multiline
                                    rows={2}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="faaNotified"
                                            checked={formData.faaNotified}
                                            onChange={handleChange}
                                        />
                                    }
                                    label="FAA Notified"
                                />
                            </Grid>

                            {/* Attachments Section */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                                    Attachments (Optional)
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                                    Upload photos, videos, or documents related to this incident (PDF, images, or videos up to 20MB each)
                                </Typography>

                                {uploadError && (
                                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError(null)}>
                                        {uploadError}
                                    </Alert>
                                )}

                                {/* Uploaded Files List */}
                                {attachments.length > 0 && (
                                    <Box sx={{ mb: 2 }}>
                                        {attachments.map((attachment, index) => (
                                            <Box
                                                key={index}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    p: 1.5,
                                                    mb: 1,
                                                    bgcolor: 'action.hover',
                                                    borderRadius: 1
                                                }}
                                            >
                                                <AttachIcon color="primary" fontSize="small" />
                                                <Typography variant="body2" sx={{ flex: 1 }}>
                                                    {attachment.fileName}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {new Date(attachment.uploadedAt).toLocaleTimeString()}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleRemoveAttachment(index)}
                                                    disabled={uploading}
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        ))}
                                    </Box>
                                )}

                                {/* Upload Button */}
                                <Box>
                                    <input
                                        accept="application/pdf,image/jpeg,image/jpg,image/png,video/mp4,video/quicktime"
                                        style={{ display: 'none' }}
                                        id="incident-attachments-upload"
                                        type="file"
                                        multiple
                                        onChange={handleFileSelect}
                                        disabled={uploading}
                                    />
                                    <label htmlFor="incident-attachments-upload">
                                        <Button
                                            variant="outlined"
                                            component="span"
                                            startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
                                            disabled={uploading}
                                            fullWidth
                                        >
                                            {uploading ? 'Uploading...' : 'Upload Files'}
                                        </Button>
                                    </label>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseReportDialog} disabled={uploading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleReportIncident}
                        variant="contained"
                        disabled={uploading}
                        startIcon={uploading ? <CircularProgress size={20} /> : null}
                    >
                        {uploading ? 'Uploading Files...' : 'Report Incident'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Incident Dialog */}
            <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Incident Details</DialogTitle>
                <DialogContent>
                    {selectedIncident && (
                        <Box sx={{ mt: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Incident Date & Time
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        {formatDateTime(selectedIncident.incidentDate, selectedIncident.incidentTimezone)}
                                    </Typography>
                                    {selectedIncident.incidentTimezone && (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Timezone: {selectedIncident.incidentTimezone.replace(/_/g, ' ')}
                                        </Typography>
                                    )}
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Severity
                                    </Typography>
                                    <Chip
                                        label={selectedIncident.severity}
                                        color={getSeverityColor(selectedIncident.severity)}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Type
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        {selectedIncident.incidentType}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Status
                                    </Typography>
                                    <Chip
                                        label={selectedIncident.status}
                                        color={getStatusColor(selectedIncident.status)}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Location
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        {selectedIncident.location || 'Not specified'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Description
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        {selectedIncident.description}
                                    </Typography>
                                </Grid>
                                {selectedIncident.damages && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Damages
                                        </Typography>
                                        <Typography variant="body1" gutterBottom>
                                            {selectedIncident.damages}
                                        </Typography>
                                    </Grid>
                                )}
                                {selectedIncident.injuries && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Injuries
                                        </Typography>
                                        <Typography variant="body1" gutterBottom>
                                            {selectedIncident.injuries}
                                        </Typography>
                                    </Grid>
                                )}
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        FAA Notified
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        {selectedIncident.faaNotified ? 'Yes' : 'No'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Reported By
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        {selectedIncident.reporter?.firstName} {selectedIncident.reporter?.lastName}
                                    </Typography>
                                </Grid>

                                {/* Attachments Section */}
                                {selectedIncident.attachments && selectedIncident.attachments.length > 0 && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                                            Attachments ({selectedIncident.attachments.length})
                                        </Typography>
                                        <Box sx={{ mt: 1 }}>
                                            {selectedIncident.attachments.map((attachment, index) => (
                                                <Box
                                                    key={index}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        p: 1.5,
                                                        mb: 1,
                                                        bgcolor: 'background.default',
                                                        borderRadius: 1,
                                                        border: '1px solid',
                                                        borderColor: 'divider'
                                                    }}
                                                >
                                                    <AttachIcon color="primary" fontSize="small" />
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {attachment.fileName}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Uploaded: {new Date(attachment.uploadedAt).toLocaleString()}
                                                        </Typography>
                                                    </Box>
                                                    <Tooltip title="View attachment">
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => handleViewAttachment(attachment.fileUrl)}
                                                        >
                                                            <ViewIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Download">
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            component="a"
                                                            href={attachment.fileUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            <DownloadIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Document Viewer Modal */}
            <Dialog
                open={documentViewerOpen}
                onClose={() => setDocumentViewerOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    View Attachment
                    <IconButton
                        aria-label="close"
                        onClick={() => setDocumentViewerOpen(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {viewingDocumentUrl && (
                        <>
                            {isImageFile(viewingDocumentUrl) ? (
                                <Box sx={{ textAlign: 'center' }}>
                                    <img
                                        src={viewingDocumentUrl}
                                        alt="Attachment"
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '70vh',
                                            objectFit: 'contain'
                                        }}
                                    />
                                </Box>
                            ) : (
                                <iframe
                                    src={viewingDocumentUrl}
                                    title="Attachment"
                                    style={{
                                        width: '100%',
                                        height: '70vh',
                                        border: 'none'
                                    }}
                                />
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => window.open(viewingDocumentUrl, '_blank')}
                        variant="outlined"
                    >
                        Open in New Tab
                    </Button>
                    <Button onClick={() => setDocumentViewerOpen(false)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Incident</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this incident report? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDeleteIncident} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={hideToast}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={hideToast} severity={toast.severity} sx={{ width: '100%' }}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default Incidents;
